import { type RefObject, useEffect, useRef } from 'react';

import { type SearchableText, lookupQuote } from '@/lib/documents/review-quote';

/**
 * Highlights, in the article itself, the passage each open review note quotes.
 *
 * It uses the CSS Custom Highlight API (`CSS.highlights` and `::highlight()` in `src/index.css`)
 * over the DOM the reader already shows, so the same hook serves the Markdown reader and the
 * ProseMirror editor. Nothing is written to the document: an editor mark would be serialised into
 * the Markdown and change the content hash a review is keyed on. Where the API is missing the hook
 * does nothing and the rail's own link to the passage remains.
 *
 * Both directions are linked: a card in the rail sets `focusedKey` and the reader scrolls to the
 * passage; a press on a marked passage calls `onPressNote` with its note, so the rail can bring that
 * card forward. A highlight is paint, not an element, so the press is found by hit-testing the
 * passages' client rectangles (`noteAtPoint`) on the root's own click event.
 */

export interface ReviewHighlightNote {
  /** `articleCritiqueFindingKey`, so a card in the rail and its passage name the same note. */
  key: string;
  /** The finding's quote: a slice of the Markdown source. */
  quote: string;
  /** Evidence notes are tinted with the warning hue; prose notes are neutral. */
  kind: 'evidence' | 'prose';
}

export const REVIEW_HIGHLIGHT_NAMES = {
  evidence: 'review-note-evidence',
  prose: 'review-note-prose',
  evidenceActive: 'review-note-evidence-active',
  proseActive: 'review-note-prose-active',
} as const;

/** Text that is chrome, not article: toolbars, menus, buttons, notices. */
const IGNORED =
  '[data-review-highlight-ignore], [data-selection-toolbar], [role="toolbar"], nav, button, script, style';
const BLOCK = 'p, li, h1, h2, h3, h4, h5, h6, td, th, blockquote, pre, figcaption, dt, dd';

interface Segment {
  node: Text;
  start: number;
}

function readableText(root: HTMLElement): { searchable: SearchableText; segments: Segment[] } {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode: (node) =>
      node.parentElement?.closest(IGNORED) ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT,
  });
  const segments: Segment[] = [];
  const positions: Array<number | null> = [];
  let chars = '';
  let previousBlock: Element | null = null;
  for (let node = walker.nextNode() as Text | null; node; node = walker.nextNode() as Text | null) {
    const text = node.data;
    if (!text) continue;
    const block = node.parentElement?.closest(BLOCK) ?? root;
    // Whitespace between blocks (the newlines a renderer puts between paragraphs) is layout, not text.
    if (!/\S/.test(text) && block !== previousBlock) continue;
    if (chars && block !== previousBlock) {
      chars += '\n';
      positions.push(null);
    }
    segments.push({ node, start: chars.length });
    for (let index = 0; index < text.length; index += 1) positions.push(chars.length + index);
    chars += text;
    previousBlock = block;
  }
  return { searchable: { chars, positions }, segments };
}

function segmentAt(segments: Segment[], index: number): Segment | null {
  let low = 0;
  let high = segments.length - 1;
  while (low <= high) {
    const middle = (low + high) >> 1;
    const segment = segments[middle];
    if (index < segment.start) high = middle - 1;
    else if (index >= segment.start + segment.node.data.length) low = middle + 1;
    else return segment;
  }
  return null;
}

/**
 * The DOM range of each note's passage. A note whose quote is missing, or occurs more than once,
 * gets no range: pointing at the wrong occurrence is worse than pointing at none.
 */
export function findReviewRanges(
  root: HTMLElement,
  notes: readonly ReviewHighlightNote[],
): Map<string, Range> {
  const ranges = new Map<string, Range>();
  if (!notes.length) return ranges;
  const { searchable, segments } = readableText(root);
  if (!searchable.chars) return ranges;
  for (const note of notes) {
    const found = lookupQuote(searchable, note.quote);
    if (found.status !== 'found') continue;
    const first = segmentAt(segments, found.range.from);
    const last = segmentAt(segments, found.range.to - 1);
    if (!first || !last) continue;
    const range = document.createRange();
    range.setStart(first.node, found.range.from - first.start);
    range.setEnd(last.node, found.range.to - last.start);
    ranges.set(note.key, range);
  }
  return ranges;
}

/** Pixels of slack around a passage's line boxes, so a press on a descender still counts. */
const HIT_SLOP = 1;

/**
 * The note whose passage is under a point, or null. Where passages overlap, the shorter one wins:
 * it is the more specific note.
 */
export function noteAtPoint(ranges: Map<string, Range>, x: number, y: number): string | null {
  let best: { key: string; length: number } | null = null;
  for (const [key, range] of ranges) {
    if (typeof range.getClientRects !== 'function') continue;
    const hit = Array.from(range.getClientRects()).some(
      (rect) =>
        x >= rect.left - HIT_SLOP &&
        x <= rect.right + HIT_SLOP &&
        y >= rect.top - HIT_SLOP &&
        y <= rect.bottom + HIT_SLOP,
    );
    if (!hit) continue;
    const length = range.toString().length;
    if (!best || length < best.length) best = { key, length };
  }
  return best?.key ?? null;
}

function supportsHighlights(): boolean {
  return (
    typeof CSS !== 'undefined' &&
    'highlights' in CSS &&
    typeof (globalThis as { Highlight?: unknown }).Highlight === 'function'
  );
}

function clearHighlights(): void {
  if (!supportsHighlights()) return;
  for (const name of Object.values(REVIEW_HIGHLIGHT_NAMES)) CSS.highlights.delete(name);
}

function paint(
  ranges: Map<string, Range>,
  notes: readonly ReviewHighlightNote[],
  focusedKey: string | null,
): void {
  const groups: Record<keyof typeof REVIEW_HIGHLIGHT_NAMES, Range[]> = {
    evidence: [],
    prose: [],
    evidenceActive: [],
    proseActive: [],
  };
  for (const note of notes) {
    const range = ranges.get(note.key);
    if (!range) continue;
    const active = note.key === focusedKey;
    groups[
      note.kind === 'evidence'
        ? active
          ? 'evidenceActive'
          : 'evidence'
        : active
          ? 'proseActive'
          : 'prose'
    ].push(range);
  }
  for (const [group, name] of Object.entries(REVIEW_HIGHLIGHT_NAMES)) {
    const list = groups[group as keyof typeof REVIEW_HIGHLIGHT_NAMES];
    if (!list.length) {
      CSS.highlights.delete(name);
      continue;
    }
    const highlight = new Highlight(...list);
    highlight.priority = group.endsWith('Active') ? 1 : 0;
    CSS.highlights.set(name, highlight);
  }
}

function scrollToRange(range: Range): void {
  const element = range.startContainer.parentElement;
  if (!element || typeof element.scrollIntoView !== 'function') return;
  const calm =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  element.scrollIntoView({ block: 'center', behavior: calm ? 'auto' : 'smooth' });
}

interface UseReviewHighlightsOptions {
  /** The scroll container that holds the reader or the editor. */
  rootRef: RefObject<HTMLElement | null>;
  /** Open notes only, and none when the review describes an older version of the article. */
  notes: readonly ReviewHighlightNote[];
  /** The note whose card in the rail was pressed. */
  focusedKey?: string | null;
  /** Bumped by each press, so pressing the same card again scrolls to its passage again. */
  focusRequestId?: number;
  /** Anything that means "a different article is showing", so the ranges are rebuilt. */
  resetKey?: string | null;
  /**
   * A press on a marked passage (not a drag that selects text). The reader does not scroll for a
   * focus it caused itself: the passage is already under the pointer.
   */
  onPressNote?: (key: string) => void;
}

/** How long a burst of DOM changes (a keystroke, a re-render) settles before the passages are re-found. */
const SETTLE_MS = 150;

export function useReviewHighlights({
  rootRef,
  notes,
  focusedKey = null,
  focusRequestId = 0,
  resetKey = null,
  onPressNote,
}: UseReviewHighlightsOptions): void {
  const notesRef = useRef(notes);
  notesRef.current = notes;
  const focusedRef = useRef(focusedKey);
  focusedRef.current = focusedKey;
  const rangesRef = useRef<Map<string, Range>>(new Map());
  const onPressRef = useRef(onPressNote);
  onPressRef.current = onPressNote;
  /** The note a press in the text just focused, so the scroll effect leaves the view where it is. */
  const pressedKeyRef = useRef<string | null>(null);
  const signature = notes.map((note) => `${note.key}:${note.kind}`).join('|');

  useEffect(() => {
    const root = rootRef.current;
    if (!root || !supportsHighlights()) return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const apply = () => {
      timer = undefined;
      const current = notesRef.current;
      rangesRef.current = findReviewRanges(root, current);
      paint(rangesRef.current, current, focusedRef.current);
    };
    apply();
    const observer = new MutationObserver(() => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(apply, SETTLE_MS);
    });
    observer.observe(root, { childList: true, subtree: true, characterData: true });
    return () => {
      observer.disconnect();
      if (timer) clearTimeout(timer);
      rangesRef.current = new Map();
      clearHighlights();
    };
  }, [rootRef, signature, resetKey]);

  useEffect(() => {
    if (!supportsHighlights()) return;
    paint(rangesRef.current, notesRef.current, focusedKey);
  }, [focusedKey]);

  useEffect(() => {
    if (!focusedKey || !focusRequestId) return;
    if (pressedKeyRef.current === focusedKey) {
      pressedKeyRef.current = null;
      return;
    }
    const range = rangesRef.current.get(focusedKey);
    if (range) scrollToRange(range);
  }, [focusedKey, focusRequestId]);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const onClick = (event: MouseEvent) => {
      const press = onPressRef.current;
      if (!press || event.button !== 0 || !rangesRef.current.size) return;
      // A drag that selected text is a selection (the AI edit bar's job), not a press on a note.
      const selection = typeof window.getSelection === 'function' ? window.getSelection() : null;
      if (selection && !selection.isCollapsed) return;
      const key = noteAtPoint(rangesRef.current, event.clientX, event.clientY);
      if (!key) return;
      pressedKeyRef.current = key;
      press(key);
    };
    root.addEventListener('click', onClick);
    return () => root.removeEventListener('click', onClick);
  }, [rootRef]);
}
